import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import API from '../../API';
import ConfigContext from '../../contexts/ConfigContext';
import CategoryContext from '../../contexts/CategoryContext';
import Loading from '../widgets/Loading';
import { getTenantById } from '../../tenants';

const centeredPaneSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  px: 2,
} as const;

type Props = {
  children: React.ReactNode;
};

const TenantRoute: React.FC<Props> = ({ children }) => {
  const { store } = useParams<{ store: string }>();
  const { setConfig } = useContext(ConfigContext);
  const { setCategoryState, doCategoryRefresh } = useContext(CategoryContext);
  const [readyStore, setReadyStore] = useState<string | undefined>();

  useEffect(() => {
    if (!getTenantById(store)) {
      return;
    }

    // Clear stale categories synchronously so the previous tenant's icons
    // don't flash through while the new ones load.
    setCategoryState({ categories: [] });

    let cancelled = false;
    (async () => {
      try {
        await API.setActiveStore(store);
        const cfg = await API.getClientConfig('client');
        if (cancelled) return;
        setConfig(cfg);
        await doCategoryRefresh();
        if (cancelled) return;
        setReadyStore(store);
      } catch (err) {
        // Unblock the UI even on failure — show whatever we have rather
        // than spinning forever.
        console.error('TenantRoute load failed', err);
        if (!cancelled) setReadyStore(store);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store, setConfig, setCategoryState, doCategoryRefresh]);

  if (!getTenantById(store)) {
    return (
      <Box sx={centeredPaneSx}>
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          Unknown shop.
        </Typography>
      </Box>
    );
  }

  if (readyStore !== store) {
    return <Loading />;
  }

  return <>{children}</>;
};

export default TenantRoute;
